import invariant from "tiny-invariant"

import { connectErrorHandler } from "@/helpers/error"
import { useToast } from "@/hooks/toastHook"
import { useCallPanelLogger } from "@/hooks/useLogger"

import { useAgentState } from "."
import { CUSTOM_PSEUDO_ACW_STATUS } from "./connect-listeners"

export function useDisplayAgentStatus() {
  const { agentRef, state } = useAgentState()
  const log = useCallPanelLogger()
  const { error: toastError } = useToast()

  /* Change agent Connect status and send the event to update the state machine `displayAgentStatus` */
  async function setAgentStatus(
    status: "offline" | "online",
    options?: ToggleOptions,
  ) {
    invariant(agentRef.current)
    try {
      await toggleConnectAgentStatus(agentRef.current, status, options)

      return true
    } catch (error) {
      log.error(error, { status })
      toastError("Unable to change agent status")

      return false
    }
  }

  return { setAgentStatus, status: state.context.displayStatus }
}

type ToggleOptions = {
  enqueueNextState?: boolean
}

export function toggleConnectAgentStatus(
  agent: connect.Agent,
  status: "offline" | "online" | "pseudo_acw",
  options?: ToggleOptions,
) {
  return new Promise((resolve, reject) => {
    const newStateType = getConnectStatusName(status)
    const newState = agent
      .getAgentStates()
      .find((state) => state.name === newStateType)

    if (!newState)
      return reject(new Error(`Invalid agent state ${newStateType}`))

    agent.setState(
      newState,
      {
        success: () => resolve(`Success: ${newStateType}`),
        failure: (err) =>
          reject(
            connectErrorHandler(err, `Failed to set agent status ${status}"`),
          ),
      },
      options,
    )
  })
}

function getConnectStatusName(status: "offline" | "online" | "pseudo_acw") {
  switch (status) {
    case "offline":
      return "Offline"
    case "online":
      return "Available"
    // Custom state https://docs.aws.amazon.com/connect/latest/adminguide/agent-custom.html
    case "pseudo_acw":
      return CUSTOM_PSEUDO_ACW_STATUS
    default:
      throw new Error("Invalid status")
  }
}
